/**
 * useMentionAutocomplete — Manages @ mention detection, candidate fetching,
 * and keyboard navigation for the mention autocomplete palette.
 *
 * Detects @ trigger in text (at start of line or after whitespace),
 * builds a combined list of workspace members + registered agents + search contacts,
 * and exposes keyboard navigation + insert callback.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '../../../../api/auth';
import { companiesApi, contactsApi } from '../../../../api/crm';
import { topicsApi } from '../../../../api/topics';
import { useWorkspace } from '../../../../contexts/WorkspaceContext';

export interface MentionCandidate {
  id: string;
  name: string;
  email?: string;
  avatar?: string | null;
  kind: 'user' | 'agent' | 'contact' | 'company' | 'file';
}

function formatFileSizeCompact(bytes: number): string {
  if (!bytes || bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface UseMentionAutocompleteResult {
  isOpen: boolean;
  filter: string;
  selectedIndex: number;
  filteredCandidates: MentionCandidate[];
  setIsOpen: (open: boolean) => void;
  setSelectedIndex: (index: number) => void;
  /** Call when text changes to detect/clear @ trigger */
  handleTextChange: (text: string, cursorPos: number) => void;
  /** Insert the selected mention; returns new message text */
  insertMention: (candidate: MentionCandidate, currentMessage: string, anchorPos: number, cursorPos: number) => string;
  /** Keyboard handler — returns true if the event was consumed */
  handleKeyDown: (key: string, currentMessage: string, anchorPos: number, cursorPos: number) => { consumed: boolean; newMessage?: string };
  /** Current anchor position (index of @ in message string) */
  anchorPos: number;
}

export function useMentionAutocomplete(topicId?: string): UseMentionAutocompleteResult {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;

  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [anchorPos, setAnchorPos] = useState(-1);
  const [searchResults, setSearchResults] = useState<MentionCandidate[]>([]);

  const { data: membersData } = useQuery({
    queryKey: ['workspace-members', wsId],
    queryFn: () => authApi.listMembers(wsId!, 100),
    enabled: !!wsId,
    staleTime: 60_000,
    retry: 1,
  });

  const { data: registryAgents } = useQuery({
    queryKey: ['agent-registry'],
    queryFn: async () => {
      const res = await fetch('/api/orchestration/agents', { credentials: 'include' });
      if (!res.ok) return [];
      return res.json() as Promise<Array<{ agent_id: string; agent_name: string; agent_type: string; capabilities: unknown }>>;
    },
    staleTime: 120_000,
  });

  const { data: topicFilesData } = useQuery({
    queryKey: ['topic-files', topicId],
    queryFn: () => topicsApi.getTopicFiles(topicId!),
    enabled: !!topicId,
    staleTime: 30_000,
  });

  // Debounced CRM search for companies/contacts when filter has 2+ chars
  useEffect(() => {
    if (!isOpen || filter.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const [companiesRes, contactsRes] = await Promise.all([
          companiesApi.list({ search: filter, pageSize: 5 }),
          contactsApi.list({ search: filter, pageSize: 5 }),
        ]);
        const candidates: MentionCandidate[] = [];

        for (const c of companiesRes.data) {
          candidates.push({
            id: c.id,
            name: c.name,
            email: c.domain ?? undefined,
            kind: 'company',
          });
        }

        for (const c of contactsRes.data) {
          const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email?.split('@')[0] || 'Unknown';
          candidates.push({
            id: c.id,
            name,
            email: c.email,
            kind: 'contact',
          });
        }

        setSearchResults(candidates);
      } catch {
        setSearchResults([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [isOpen, filter]);

  const allCandidates = useMemo((): MentionCandidate[] => {
    const candidates: MentionCandidate[] = [];
    for (const m of membersData?.data ?? []) {
      candidates.push({
        id: m.userId,
        name: m.user.name || m.user.email.split('@')[0],
        email: m.user.email,
        avatar: m.user.avatar,
        kind: 'user',
      });
    }
    for (const a of registryAgents ?? []) {
      candidates.push({ id: a.agent_id, name: a.agent_name, kind: 'agent' });
    }
    for (const f of topicFilesData?.files ?? []) {
      const ext = f.filename.split('.').pop()?.toUpperCase() || '';
      const size = formatFileSizeCompact(Number(f.file_size));
      candidates.push({
        id: `file:${f.id}`,
        name: f.filename,
        email: [ext, size].filter(Boolean).join(' · ') || undefined,
        kind: 'file',
      });
    }
    return candidates;
  }, [membersData, registryAgents, topicFilesData]);

  const filteredCandidates = useMemo((): MentionCandidate[] => {
    if (!isOpen) return [];
    const q = filter.toLowerCase();
    // Filter local candidates (members + agents)
    const local = q
      ? allCandidates.filter(c =>
          c.name.toLowerCase().includes(q) || (c.email?.toLowerCase().includes(q))
        )
      : allCandidates;
    // Merge with search results, deduplicating by id and email
    const seenIds = new Set(local.map(c => c.id));
    const seenEmails = new Set(local.filter(c => c.email).map(c => c.email!.toLowerCase()));
    const merged = [...local];
    for (const s of searchResults) {
      if (seenIds.has(s.id)) continue;
      if (s.email && seenEmails.has(s.email.toLowerCase())) continue;
      merged.push(s);
      seenIds.add(s.id);
      if (s.email) seenEmails.add(s.email.toLowerCase());
    }
    return merged.slice(0, 12);
  }, [isOpen, filter, allCandidates, searchResults]);

  const handleTextChange = useCallback((text: string, cursorPos: number) => {
    const beforeCursor = text.slice(0, cursorPos);
    const atMatch = beforeCursor.match(/(^|[\s])@([^\s]*)$/);
    if (atMatch) {
      const anchor = beforeCursor.lastIndexOf('@');
      setIsOpen(true);
      setFilter(atMatch[2]);
      setSelectedIndex(0);
      setAnchorPos(anchor);
    } else {
      setIsOpen(false);
    }
  }, []);

  const insertMention = useCallback((
    candidate: MentionCandidate,
    currentMessage: string,
    anchor: number,
    cursorPos: number,
  ): string => {
    const before = currentMessage.slice(0, anchor);
    const after = currentMessage.slice(cursorPos);
    const mention = `@[${candidate.name}](${candidate.id}) `;
    setIsOpen(false);
    setFilter('');
    return before + mention + after;
  }, []);

  const handleKeyDown = useCallback((
    key: string,
    currentMessage: string,
    anchor: number,
    cursorPos: number,
  ): { consumed: boolean; newMessage?: string } => {
    if (!isOpen || filteredCandidates.length === 0) return { consumed: false };
    if (key === 'ArrowDown') {
      setSelectedIndex(i => (i + 1) % filteredCandidates.length);
      return { consumed: true };
    }
    if (key === 'ArrowUp') {
      setSelectedIndex(i => (i - 1 + filteredCandidates.length) % filteredCandidates.length);
      return { consumed: true };
    }
    if (key === 'Enter' || key === 'Tab') {
      const newMessage = insertMention(filteredCandidates[selectedIndex], currentMessage, anchor, cursorPos);
      return { consumed: true, newMessage };
    }
    if (key === 'Escape') {
      setIsOpen(false);
      return { consumed: true };
    }
    return { consumed: false };
  }, [isOpen, filteredCandidates, selectedIndex, insertMention]);

  return {
    isOpen,
    filter,
    selectedIndex,
    filteredCandidates,
    setIsOpen,
    setSelectedIndex,
    handleTextChange,
    insertMention,
    handleKeyDown,
    anchorPos,
  };
}
